import { useState } from 'react';
import { bluesScale, formatNum, formatPct } from '../chartSetup';

export interface FunnelItem {
    label: string;
    count: number;
    pct?: number | null;
    dropPct?: number | null;
    // Explanation of what the level means; when present the label becomes hoverable
    // and reveals this text in a tooltip.
    description?: string | null;
    href?: string | null;
}

interface Props {
    items: FunnelItem[];
    // Width (%) reserved for the right-hand label column. 0 hides labels.
    labelWidth?: number;
    minWidth?: number;
    maxWidth?: number;
}

// A stylised, centered funnel: bars narrow by rank (not strictly by value, matching
// the design), shaded light→dark blue, with the count centered and the level label
// to the right. Hovering a bar reveals a tooltip with share + drop figures.
const CenteredFunnel = ({ items, labelWidth = 34, minWidth = 42, maxWidth = 96 }: Props) => {
    const [hover, setHover] = useState<number | null>(null);
    const [labelHover, setLabelHover] = useState<number | null>(null);
    const n = items.length;
    const colors = bluesScale(n);

    return (
        <div className="w-100">
            {items.map((it, i) => {
                const width = n <= 1 ? maxWidth : maxWidth - (i / (n - 1)) * (maxWidth - minWidth);
                const isDark = i / Math.max(1, n - 1) > 0.45;
                return (
                    <div key={`${it.label}-${i}`}>
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex justify-content-center"
                                style={{ flex: 1, position: 'relative' }}>
                                <div
                                    onMouseEnter={() => setHover(i)}
                                    onMouseLeave={() => setHover(null)}
                                    style={{
                                        width: `${width}%`,
                                        background: colors[i],
                                        color: isDark ? '#ffffff' : '#0d2b45',
                                        borderRadius: 8,
                                        padding: '12px 16px',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                        cursor: it.href ? 'pointer' : 'default',
                                    }}>
                                    {it.href ? (
                                        <a
                                            href={it.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                display: 'block',
                                            }}
                                            title="Open directory">
                                            {formatNum(it.count)}
                                        </a>
                                    ) : (
                                        formatNum(it.count)
                                    )}
                                </div>
                                {hover === i && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            marginBottom: 8,
                                            background: '#1f2733',
                                            color: '#fff',
                                            borderRadius: 8,
                                            padding: '8px 12px',
                                            fontSize: 12,
                                            whiteSpace: 'nowrap',
                                            zIndex: 10,
                                            boxShadow: '0 6px 18px rgba(0,0,0,.22)',
                                        }}>
                                        <div style={{ color: '#5bc0eb', fontWeight: 700, marginBottom: 2 }}>
                                            {it.label}
                                        </div>
                                        <div>
                                            {formatNum(it.count)} alumni
                                            {it.pct !== null && it.pct !== undefined
                                                ? ` · ${formatPct(it.pct, 0)} of total`
                                                : ''}
                                        </div>
                                        {/* {it.dropPct !== null && it.dropPct !== undefined && (
                                            <div style={{ color: '#ffb86b' }}>
                                                ↓ {formatPct(it.dropPct, 0)} drop from previous level
                                            </div>
                                        )} */}
                                    </div>
                                )}
                            </div>
                            {labelWidth > 0 && (
                                <div
                                    style={{
                                        width: `${labelWidth}%`,
                                        paddingLeft: 16,
                                        color: '#6b7785',
                                        fontSize: 13,
                                        position: 'relative',
                                    }}>
                                    {it.href ? (
                                        <a
                                            href={it.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onMouseEnter={() => setLabelHover(i)}
                                            onMouseLeave={() => setLabelHover(null)}
                                            style={{
                                                color: '#2f6fdb',
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                borderBottom: it.description ? '1px dashed #9bb8e8' : 'none',
                                            }}
                                            title="Open directory">
                                            {it.label}
                                        </a>
                                    ) : it.description ? (
                                        <span
                                            onMouseEnter={() => setLabelHover(i)}
                                            onMouseLeave={() => setLabelHover(null)}
                                            style={{
                                                borderBottom: '1px dashed #b6c2ce',
                                                cursor: 'default',
                                            }}>
                                            {it.label}
                                        </span>
                                    ) : (
                                        it.label
                                    )}
                                    {labelHover === i && it.description && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                bottom: '100%',
                                                left: 16,
                                                marginBottom: 8,
                                                background: '#1f2733',
                                                color: '#fff',
                                                borderRadius: 8,
                                                padding: '8px 12px',
                                                fontSize: 12,
                                                lineHeight: 1.5,
                                                width: 260,
                                                zIndex: 10,
                                                boxShadow: '0 6px 18px rgba(0,0,0,.22)',
                                            }}>
                                            <div style={{ color: '#5bc0eb', fontWeight: 700, marginBottom: 2 }}>
                                                {it.label}
                                            </div>
                                            <div>{it.description}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {i < n - 1 && (
                            <div className="d-flex align-items-center">
                                <div className="d-flex justify-content-center" style={{ flex: 1 }}>
                                    <div style={{ width: 1, height: 14, background: '#cbd5e1' }} />
                                </div>
                                {labelWidth > 0 && <div style={{ width: `${labelWidth}%` }} />}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CenteredFunnel;
